import { css, SerializedStyles } from '@emotion/core'
import { Icons } from 'bold-ui'
import { blue, green, orange, pink, red, turquoise, yellow } from 'bold-ui/lib/styles/colors'
import { SubtipoRecord } from 'components/form/field/select/SubtipoAtendimentoSelectField/SubtipoAtendimentoSelectField'
import {
  CategoriaProblemaEnum,
  ClassificacaoRisco,
  OrigemAtendimento,
  PageInfo,
  SubtipoAtendimentoHistoricoClinico,
  TipoAtendimentoHistoricoClinico,
  TipoAtendimentoProfissional,
  TurnoEnum,
} from 'graphql/types.generated'
import { TipoAtendimentoHistorico } from 'types/enums'

export interface HistoricoCabecalhoModel {
  idAtendRecente?: ID
  idAtendProcessado?: ID
  codigoUnicoRegistro?: string
  dataAtendimento?: Instant
  turno?: TurnoEnum
  tipoApresentacao?: TipoAtendimentoHistoricoClinico
  isAtendRecente: boolean
  tipoAtendProf?: TipoAtendimentoProfissional
  coSubtipoAtendimento?: number
  origemAtendimento?: OrigemAtendimento
  cpfCnsCidadao?: string
  classificacaoRisco?: ClassificacaoRisco
  idadeGestacional?: {
    dias: number
    semanas: number
  }
  dadosClinicosHeader?: string
  condicoesAvaliadas?: string[]
  profissional?: {
    id: string
    nome: string
  }
  cbo?: {
    id: string
    nome: string
    cbo2002: string
  }
  unidadeSaude?: {
    id: string
    nome?: string
    cnes: string
  }
  equipe?: {
    id: string
    ine: string
    nome: string
  }
  hasPrescricaoMedicamento?: boolean
  hasOrientacao?: boolean
  hasAtestado?: boolean
  hasLembrete?: boolean
  hasAlergia?: boolean
  hasSolicitacaoExame?: boolean
  hasResultadoExame?: boolean

  // Dados clínicos agregados usados na listagem do histórico
  ciapNomeDescricao?: string
  cidNomeDescricao?: string
  vacinaNomeSigla?: string
  procedimentoNomeCodigo?: string
  cid10PrincipalFaeNomeCodigo?: string
  cid10SecundarioUmFaeNomeCodigo?: string
  cid10SecundarioDoisFaeNomeCodigo?: string
  condicoesVacinacao?: HistoricoCondicoesVacinacao
}

export type HistoricoCondicoesVacinacao = {
  idAtend: ID
  isViajante?: boolean
  isGestante?: boolean
  isPuerpera?: boolean
  isComunicanteHanseniase?: boolean
}

export type HistoricoAtendimentoListagemTableModel = {
  content: HistoricoCabecalhoModel[]
  pageInfo: PageInfo
}

export type ProcedimentoHistoricoModel = {
  codigo?: string
  descricao: string
  categoriaProblema: CategoriaProblemaEnum
}
interface SubtipoAtendimentoTag {
  id: ID
  nome: SubtipoAtendimentoHistoricoClinico
  tagColor: string
}

const consultaSubtiposTags: SubtipoAtendimentoTag[] = [
  {
    id: 1,
    nome: SubtipoAtendimentoHistoricoClinico.CONSULTA_PROGRAMADA_CUIDADO_CONTINUADO,
    tagColor: blue.c40,
  },
  {
    id: 2,
    nome: SubtipoAtendimentoHistoricoClinico.CONSULTA,
    tagColor: blue.c40,
  },
  {
    id: 4,
    nome: SubtipoAtendimentoHistoricoClinico.ESCUTA_INICIAL_ORIENTACAO,
    tagColor: blue.c40,
  },
  {
    id: 5,
    nome: SubtipoAtendimentoHistoricoClinico.CONSULTA_DIA,
    tagColor: blue.c40,
  },
  {
    id: 6,
    nome: SubtipoAtendimentoHistoricoClinico.URGENCIA,
    tagColor: red.c40,
  },
  {
    id: 7,
    nome: SubtipoAtendimentoHistoricoClinico.PROGRAMADA,
    tagColor: blue.c40,
  },
  {
    id: 8,
    nome: SubtipoAtendimentoHistoricoClinico.NAO_PROGRAMADA,
    tagColor: blue.c40,
  },
  {
    id: 9,
    nome: SubtipoAtendimentoHistoricoClinico.VISITA_DOMICILIAR_POS_OBITO,
    tagColor: blue.c40,
  },
]

const ADSubtiposTags: SubtipoAtendimentoTag[] = [
  {
    id: 7,
    nome: SubtipoAtendimentoHistoricoClinico.PROGRAMADA,
    tagColor: green.c40,
  },
  {
    id: 8,
    nome: SubtipoAtendimentoHistoricoClinico.NAO_PROGRAMADA,
    tagColor: green.c40,
  },
  {
    id: 9,
    nome: SubtipoAtendimentoHistoricoClinico.VISITA_DOMICILIAR_POS_OBITO,
    tagColor: green.c40,
  },
]

const visitaSubtiposTags: SubtipoAtendimentoTag[] = [
  {
    id: 1,
    nome: SubtipoAtendimentoHistoricoClinico.VISITA_REALIZADA,
    tagColor: green.c40,
  },
  {
    id: 2,
    nome: SubtipoAtendimentoHistoricoClinico.VISITA_RECUSADA,
    tagColor: green.c40,
  },
  {
    id: 3,
    nome: SubtipoAtendimentoHistoricoClinico.CIDADAO_AUSENTE,
    tagColor: green.c40,
  },
]

const consumoAlimentarSubtiposTags: SubtipoAtendimentoTag[] = [
  {
    id: 1,
    nome: SubtipoAtendimentoHistoricoClinico.CRIANCAS_MENORES_6_MESES,
    tagColor: turquoise.c40,
  },
  {
    id: 2,
    nome: SubtipoAtendimentoHistoricoClinico.CRIANCAS_6_A_23_MESES,
    tagColor: turquoise.c40,
  },
  {
    id: 3,
    nome: SubtipoAtendimentoHistoricoClinico.CIDADAOS_MAIORES_2_ANOS,
    tagColor: turquoise.c40,
  },
]

const atividadeColetivaSubtiposTags: SubtipoAtendimentoTag[] = [
  {
    id: 4,
    nome: SubtipoAtendimentoHistoricoClinico.EDUCACAO_SAUDE,
    tagColor: turquoise.c40,
  },
  {
    id: 5,
    nome: SubtipoAtendimentoHistoricoClinico.ATENDIMENTO_GRUPO,
    tagColor: turquoise.c40,
  },
  {
    id: 6,
    nome: SubtipoAtendimentoHistoricoClinico.AVALIACAO_PROCEDIMENTO_COLETIVO,
    tagColor: turquoise.c40,
  },
  {
    id: 7,
    nome: SubtipoAtendimentoHistoricoClinico.MOBILIZACAO_SOCIAL,
    tagColor: turquoise.c40,
  },
]

interface TipoAtendimentoHistoricoProps {
  descricao: string
  iconColor: string
  barColor: string
  icon: Icons
  tags?: SubtipoAtendimentoTag[]
}

export const tipoAtendimentoHistoricoData: Record<TipoAtendimentoHistoricoClinico, TipoAtendimentoHistoricoProps> = {
  [TipoAtendimentoHistoricoClinico.CONSULTA]: {
    descricao: TipoAtendimentoHistorico.CONSULTA,
    barColor: blue.c40,
    iconColor: blue.c40,
    icon: 'nurse',
    tags: consultaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.PUERICULTURA]: {
    descricao: TipoAtendimentoHistorico.PUERICULTURA,
    barColor: blue.c40,
    iconColor: blue.c40,
    icon: 'nurse',
    tags: consultaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.PRE_NATAL]: {
    descricao: TipoAtendimentoHistorico.PRE_NATAL,
    barColor: blue.c40,
    iconColor: blue.c40,
    icon: 'nurse',
    tags: consultaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.PUERPERIO]: {
    descricao: TipoAtendimentoHistorico.PUERPERIO,
    barColor: blue.c40,
    iconColor: blue.c40,
    icon: 'nurse',
    tags: consultaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.ESCUTA_INICIAL]: {
    descricao: TipoAtendimentoHistorico.ESCUTA_INICIAL,
    barColor: pink.c40,
    iconColor: pink.c40,
    icon: 'nurse',
  },
  [TipoAtendimentoHistoricoClinico.ATENDIMENTO_ODONTOLOGICO]: {
    descricao: TipoAtendimentoHistorico.ATENDIMENTO_ODONTOLOGICO,
    barColor: orange.c60,
    iconColor: orange.c40,
    icon: 'nurse',
    tags: consultaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.ATENDIMENTO_DOMICILIAR]: {
    descricao: TipoAtendimentoHistorico.ATENDIMENTO_DOMICILIAR,
    barColor: green.c60,
    iconColor: green.c40,
    icon: 'houseOutline',
    tags: ADSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.VISITA_DOMICILIAR]: {
    descricao: TipoAtendimentoHistorico.VISITA_DOMICILIAR_TERRITORIAL,
    barColor: green.c60,
    iconColor: green.c40,
    icon: 'houseOutline',
    tags: visitaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.ATENDIMENTO_PROCEDIMENTO]: {
    descricao: TipoAtendimentoHistorico.ATENDIMENTO_PROCEDIMENTO,
    barColor: yellow.c60,
    iconColor: yellow.c40,
    icon: 'bandageOutline',
  },
  [TipoAtendimentoHistoricoClinico.VACINACAO]: {
    descricao: TipoAtendimentoHistorico.VACINACAO,
    barColor: yellow.c60,
    iconColor: yellow.c40,
    icon: 'needleOutline',
  },
  [TipoAtendimentoHistoricoClinico.ATIVIDADE_COLETIVA]: {
    descricao: TipoAtendimentoHistorico.ATIVIDADE_COLETIVA,
    barColor: turquoise.c60,
    iconColor: turquoise.c40,
    icon: 'imageOutline',
    tags: atividadeColetivaSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.CONSUMO_ALIMENTAR]: {
    descricao: TipoAtendimentoHistorico.MARCADORES_CONSUMO_ALIMENTAR,
    barColor: turquoise.c60,
    iconColor: turquoise.c40,
    icon: 'imageOutline',
    tags: consumoAlimentarSubtiposTags,
  },
  [TipoAtendimentoHistoricoClinico.AVALIACAO_ELEGIBILIDADE]: {
    descricao: TipoAtendimentoHistorico.AVALIACAO_ELEGIBILIDADE,
    barColor: turquoise.c60,
    iconColor: turquoise.c40,
    icon: 'imageOutline',
  },
  [TipoAtendimentoHistoricoClinico.ZIKA]: {
    descricao: TipoAtendimentoHistorico.SINDOME_NEUROLOGICA_ZIKA_MICROCEFALIA,
    barColor: turquoise.c60,
    iconColor: turquoise.c40,
    icon: 'imageOutline',
  },
}

export const categoriaProcedimentoEnum: Record<CategoriaProblemaEnum, string> = {
  [CategoriaProblemaEnum.CIAP]: 'CIAP',
  [CategoriaProblemaEnum.CID10]: 'CID10',
  [CategoriaProblemaEnum.OUTRO]: 'Outro',
  [CategoriaProblemaEnum.EXAME]: 'Exame',
  [CategoriaProblemaEnum.CID10_PRINCIPAL]: 'CID10 - Principal',
  [CategoriaProblemaEnum.CID10_SECUNDARIO_UM]: 'CID10 - Secundário 1',
  [CategoriaProblemaEnum.CID10_SECUNDARIO_DOIS]: 'CID10 - Secundário 2',
}

export interface EscutaHistoricoTagModel {
  style?: SerializedStyles
  descricao: string
}

const escutaHistoricoTagStyles = {
  alta: css`
    background-color: ${red.c40};
    color: white;
  `,
  baixa: css`
    background-color: ${green.c40};
    color: white;
  `,
  intermediaria: css`
    background-color: ${orange.c40};
    color: white;
  `,
  naoAguda: css`
    background-color: ${blue.c40};
    color: white;
  `,
}

export const recordEscutaHistoricoTagStyle: Record<ClassificacaoRisco, EscutaHistoricoTagModel> = {
  [ClassificacaoRisco.NAO_CLASSIFICADO]: { style: null, descricao: '' },
  [ClassificacaoRisco.PEC_ALTA]: { style: escutaHistoricoTagStyles.alta, descricao: 'Risco alto' },
  [ClassificacaoRisco.PEC_BAIXA]: { style: escutaHistoricoTagStyles.baixa, descricao: 'Risco baixo' },
  [ClassificacaoRisco.PEC_INTERMEDIARIA]: {
    style: escutaHistoricoTagStyles.intermediaria,
    descricao: 'Risco intermediário',
  },
  [ClassificacaoRisco.PEC_NAO_AGUDA]: { style: escutaHistoricoTagStyles.naoAguda, descricao: 'Não agudo' },
}

export const accordionBloqueadoMensagem: Partial<Record<SubtipoAtendimentoHistoricoClinico, string>> = {
  [SubtipoAtendimentoHistoricoClinico.CIDADAO_AUSENTE]:
    SubtipoRecord[SubtipoAtendimentoHistoricoClinico.CIDADAO_AUSENTE],
  [SubtipoAtendimentoHistoricoClinico.VISITA_RECUSADA]: 'Atendimento recusado',
}
